import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { switchMap, tap, catchError, EMPTY } from 'rxjs'

import { DialogRef, DialogService } from '@hyperkit'

import { CompanyService } from '../../../modules/shared/services/company.service'
import { UserService } from '../../../modules/shared/services/user.service'
import {
  TrackingEventEnum,
  TrackingService,
} from '../../../tracking/tracking.service'

import { requireCheckboxesToBeCheckedValidator } from './required-checkbox-validator'

@Component({
  standalone: false,
  selector: 'reactivate-dialog',
  templateUrl: './reactivate-dialog.component.html',
  styleUrls: ['./reactivate-dialog.component.scss'],
})
export class ReactivateDialogComponent implements OnInit, AfterContentChecked {
  private userService = inject(UserService)
  dialogRef = inject(DialogRef)
  dialog = inject(DialogService)
  private companyService = inject(CompanyService)
  private trackingService = inject(TrackingService)
  private cdref = inject(ChangeDetectorRef)

  private destroyRef = inject(DestroyRef)

  public error: string
  public submitted = false
  public form: FormGroup
  public showCustomReason = false

  // Use observable directly instead of component property
  public profile$ = this.userService.profile$

  public reactivationReasons: Array<string> = [
    'Build / mature Threat Hunting program',
    'Supplement in-house Hunting',
    'Leverage Hunt Packages with current SIEM & EDR',
    'Internal Push from Management',
    'Other',
  ]

  public ngOnInit(): void {
    this.initForm()
    this.loadProfile()
  }

  private initForm(): void {
    this.form = new FormGroup({
      checkboxes: new FormGroup({}, requireCheckboxesToBeCheckedValidator()),
      additionalInfo: new FormControl('', Validators.required),
    })

    this.reactivationReasons.forEach((item) => {
      ;(this.form.controls['checkboxes'] as FormGroup).addControl(
        item,
        new FormControl(false),
      )
    })
  }

  private loadProfile(): void {
    this.userService
      .loadProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: (err) => {
          console.error('Failed to load profile:', err)
          this.error = 'Failed to load profile. Please try again.'
        },
      })
  }

  public ngAfterContentChecked(): void {
    this.cdref.detectChanges()
  }

  public get checkboxGroup(): FormGroup {
    return this.form.controls['checkboxes'] as FormGroup
  }

  public onReactivate(): void {
    const currentProfile = this.userService.getCurrentProfile()

    this.companyService
      .reactivateCompany()
      .pipe(
        // First track the event
        tap(() => {
          this.trackingService.trackEvent(
            TrackingEventEnum.COMMUNITY_REACTIVATION_REACTION,
            {
              reason: this.form.value,
              user: currentProfile,
            },
          )
          this.submitted = true
        }),
        // Then refresh the profile
        switchMap(() => this.userService.refreshProfile()),
        // Handle errors
        catchError((error) => {
          this.error = error.message || 'Failed to reactivate company'
          this.submitted = false
          return EMPTY
        }),
        // Auto-cleanup
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        error: (err) => {
          // This handles any errors not caught by catchError
          console.error('Reactivation error:', err)
        },
      })
  }

  public returnToResearch(): void {
    this.dialogRef.close(true)
  }

  public onClose(): void {
    this.dialogRef.close()
  }
}
